// contexts/ThemeContext.js
// ניהול ערכת נושא — localStorage key: 'madad_theme'
// ברירת מחדל: 'classic'
// TODO: migrate to Backend API when available

import { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import rtlPlugin from 'stylis-plugin-rtl';
import { prefixer } from 'stylis';
import {
  buildMuiTheme,
  themeDefinitions,
  DEFAULT_THEME_KEY,
  THEME_STORAGE_KEY,
} from '../config/themes';

// =============================================================================
// Emotion RTL Cache — ליצירה פעם אחת
// =============================================================================
const rtlCache = createCache({
  key: 'madad-rtl',
  stylisPlugins: [prefixer, rtlPlugin],
});

// =============================================================================
// Context
// =============================================================================
const ThemeContext = createContext(null);

export function useThemeContext() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeContext must be used within ThemeContextProvider');
  }
  return context;
}

// =============================================================================
// Helper: טעינת theme מ-localStorage
// =============================================================================
function loadThemeKey() {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored && themeDefinitions[stored]) {
      return stored;
    }
  } catch (e) {
    // localStorage לא זמין
  }
  return DEFAULT_THEME_KEY;
}

// =============================================================================
// Provider
// =============================================================================
export function ThemeContextProvider({ children }) {
  const [themeKey, setThemeKeyState] = useState(loadThemeKey);

  // שינוי theme — שומר ב-localStorage + state
  const setThemeKey = useCallback((key) => {
    if (!themeDefinitions[key]) return;
    setThemeKeyState(key);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, key);
    } catch (e) {
      // localStorage לא זמין — ההגדרה תאבד בסגירת הדפדפן
    }
  }, []);

  // בניית MUI theme מהגדרות ה-theme הנוכחי
  const muiTheme = useMemo(() => buildMuiTheme(themeKey), [themeKey]);

  // מידע על ה-theme הנוכחי
  const currentThemeDef = themeDefinitions[themeKey] || themeDefinitions[DEFAULT_THEME_KEY];
  const isDarkMode = currentThemeDef.mode === 'dark';

  // עדכון data-theme-mode על html — לשימוש ב-CSS כמו focus-visible ב-accessibility.js
  useEffect(() => {
    document.documentElement.setAttribute('data-theme-mode', currentThemeDef.mode);
  }, [currentThemeDef.mode]);

  const value = useMemo(() => ({
    themeKey,
    setThemeKey,
    isDarkMode,
    themeDef: currentThemeDef,
  }), [themeKey, setThemeKey, isDarkMode, currentThemeDef]);

  return (
    <ThemeContext.Provider value={value}>
      <CacheProvider value={rtlCache}>
        <MuiThemeProvider theme={muiTheme}>
          <CssBaseline />
          {children}
        </MuiThemeProvider>
      </CacheProvider>
    </ThemeContext.Provider>
  );
}

export default ThemeContext;
